import {tx, ptx, usePageText} from '../lib/page-text';
import { ReplayCaptions } from "./program/ReplayCaptions";
import {
  ReplayPresentation,
  useReplayTimeline,
} from "./program/ReplayPresentation";
import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { DashboardDialog } from "./DashboardDialog";

interface Props {
  url: string;
  format: "hls" | "mp4";
  title?: string;
  onClose: () => void;
  onRetry?: () => void;
  refreshing?: boolean;
  retryError?: string;
  revision?: number;
  restoreFocusTo?: HTMLElement | null;
  publicReplay?: { key: string; token?: string };
  streamId?: string;
  recordingId?: string;
}

export function PlayerModal({
  url,
  format,
  title,
  onClose,
  onRetry,
  refreshing,
  retryError,
  revision,
  restoreFocusTo,
  streamId,
  publicReplay,
  recordingId,
}: Props) {
  usePageText();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const formatTime = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
  const progressKey = recordingId
    ? `kardolive-replay-progress:${recordingId}`
    : undefined;
  const [resumeAt, setResumeAt] = useState(0);
  const lastSaved = useRef(0);
  useEffect(() => {
    try {
      const saved = progressKey ? Number(localStorage.getItem(progressKey)) : 0;
      setResumeAt(Number.isFinite(saved) && saved > 0 ? saved : 0);
    } catch {
      setResumeAt(0);
    }
  }, [progressKey]);
  const saveProgress = (video: HTMLVideoElement) => {
    if (!progressKey || !Number.isFinite(video.duration) || video.duration <= 0)
      return;
    try {
      if (video.ended || video.currentTime >= video.duration - 1)
        localStorage.removeItem(progressKey);
      else if (video.currentTime > 1)
        localStorage.setItem(progressKey, String(video.currentTime));
    } catch {
      /* Playback continues when browser storage is unavailable. */
    }
  };
  const timeline = useReplayTimeline(streamId, recordingId, publicReplay);
  const mode = timeline.data?.events
    .filter((e) => e.kind === "presentation" && e.at <= time)
    .at(-1)?.payload.mode;
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let hls: Hls | undefined;
    setError(null);
    const onLoaded = () => setError(null);
    video.addEventListener("loadeddata", onLoaded);
    const loadingTimer = window.setTimeout(() => {
      if (video.readyState < 2)
        setError(
          "Playback is taking too long. Refresh the link, check your connection, or try another browser.",
        );
    }, 15000);
    if (format === "mp4" || video.canPlayType("application/vnd.apple.mpegurl"))
      video.src = url;
    else if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal)
          setError(
            "Playback was interrupted. Refresh the playback link to try again.",
          );
      });
    } else
      setError(
        "This browser cannot play this replay format. Try a different browser.",
      );
    return () => {
      window.clearTimeout(loadingTimer);
      video.removeEventListener("loadeddata", onLoaded);
      hls?.destroy();
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [url, format, revision]);
  return (
    <DashboardDialog
      title={title ?? tx("Stream replay")}
      onClose={onClose}
      className="replay-dialog"
      restoreFocusTo={restoreFocusTo}
    >
      <div
        className={`replay-stage ${mode === "board" ? "board-main" : "video-main"}`}
      >
        <video
          onTimeUpdate={(e) => {
            setTime(e.currentTarget.currentTime);
            if (Date.now() - lastSaved.current > 5000) {
              saveProgress(e.currentTarget);
              lastSaved.current = Date.now();
            }
          }}
          onPlay={() => setPlaying(true)}
          onPause={(e) => {
            setPlaying(false);
            saveProgress(e.currentTarget);
          }}
          onEnded={(e) => {
            saveProgress(e.currentTarget);
            setResumeAt(0);
          }}
          onLoadedMetadata={(e) => {
            setDuration(
              Number.isFinite(e.currentTarget.duration)
                ? e.currentTarget.duration
                : 0,
            );
            if (resumeAt >= e.currentTarget.duration - 1) setResumeAt(0);
          }}
          ref={videoRef}
          muted={muted}
          controls
          autoPlay
          playsInline
          preload="metadata"
          aria-label={tx("Replay video")}
          onError={() =>
            setError(
              "This replay could not play. Refresh the playback link to try again.",
            )
          }
        />
        {(streamId || publicReplay) &&
          recordingId &&
          timeline.data?.available && (
            <ReplayPresentation
              events={timeline.data.events}
              time={time}
              streamId={streamId}
              publicReplay={publicReplay}
              recordingId={recordingId}
              onSeek={(t) => {
                if (videoRef.current) {
                  videoRef.current.currentTime = t;
                  setTime(t);
                }
              }}
            />
          )}
      </div>
      {mode === "board" && duration > 0 && (
        <div className="replay-transport" aria-label={tx("Replay controls")}>
          <button
            className="btn outline sm"
            onClick={() => {
              const video = videoRef.current;
              if (!video) return;
              if (video.paused)
                void video
                  .play()
                  .catch(() => setError("Press play again to start playback."));
              else video.pause();
            }}
          >
            {playing ? tx("Pause replay") : tx("Play replay")}
          </button>
          <input
            type="range"
            aria-label={tx("Replay position")}
            min={0}
            max={duration}
            step={0.1}
            value={Math.min(time, duration)}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (videoRef.current) videoRef.current.currentTime = value;
              setTime(value);
            }}
          />
          <span className="muted" dir="ltr">
            {formatTime(time)} / {formatTime(duration)}
          </span>
          <button
            className="btn ghost sm"
            aria-pressed={muted}
            onClick={() => setMuted(!muted)}
          >
            {muted ? tx("Unmute replay") : tx("Mute replay")}
          </button>
        </div>
      )}
      {recordingId && (streamId || publicReplay) && (
        <ReplayCaptions
          streamId={streamId}
          recordingId={recordingId}
          publicReplay={publicReplay}
          videoRef={videoRef}
          time={time}
          onSeek={(value) => {
            if (videoRef.current) videoRef.current.currentTime = value;
            setTime(value);
          }}
        />
      )}
      {timeline.isError && (
        <p className="muted">{ptx("Presentation history could not load. The video can still play.{v0}{v1}", {v0: (" "), v1: (<button
            className="btn ghost sm"
            onClick={() => void timeline.refetch()}
          >{tx("Retry presentation")}</button>)})}</p>
      )}
      {(error || retryError) && (
        <p role="alert" className="error">
          {retryError ?? error}
        </p>
      )}
      <div className="replay-actions">
        <span className="muted">{ptx("{v0} replay", {v0: (format.toUpperCase())})}</span>
        {resumeAt > 0 && (
          <button
            className="btn outline sm"
            onClick={() => {
              const video = videoRef.current;
              if (video && Number.isFinite(video.duration)) {
                video.currentTime = Math.min(resumeAt, video.duration);
                setTime(video.currentTime);
                setResumeAt(0);
                void video.play().catch(() => {});
              }
            }}
          >{ptx("Resume at {v0}:{v1}", {v0: (Math.floor(resumeAt / 60)), v1: (String(Math.floor(resumeAt % 60)).padStart(2, "0"))})}</button>
        )}
        {onRetry && (
          <button
            className="btn outline sm"
            disabled={refreshing}
            onClick={onRetry}
          >
            {refreshing ? tx("Refreshing…") : tx("Refresh playback link")}
          </button>
        )}
      </div>
    </DashboardDialog>
  );
}
